import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { getNextStep, getPreviousStep } from '@/lib/helpers/helperFunctions'
import {
  AdditionalSignatoryFormRow,
  PlaceholdersFormValues,
  SignatureFormValues,
} from '@/pages/commercialAgreementTemplateReview/types'
import { RootState } from '@/redux/store'
import { CommercialAgreementTemplateItem } from '@/types/responses/commercial-agreement-templates'
import { SessionMetadataUser } from '@/types/responses/session-metadata'

export interface CommercialAgreementTemplateReviewState {
  isInitialised: boolean
  currentStep: CommercialAgreementTemplateReviewStep
  commercialAgreementTemplate?: CommercialAgreementTemplateItem
  placeholders: {
    formValues: PlaceholdersFormValues
  }
  signature: {
    formValues: SignatureFormValues
  }
}

const sliceName = 'commercialAgreementTemplateReview'

export const COMMERCIAL_AGREEMENT_REVIEW_STEP: Record<
  CommercialAgreementTemplateReviewStep,
  CommercialAgreementTemplateReviewStep
> = {
  overview: 'overview',
  placeholders: 'placeholders',
  pdfPreview: 'pdfPreview',
  signature: 'signature',
  commercialTermsActiveStep: 'commercialTermsActiveStep',
}

const INITIAL_STATE: CommercialAgreementTemplateReviewState = {
  isInitialised: false,
  currentStep: COMMERCIAL_AGREEMENT_REVIEW_STEP.overview,
  placeholders: {
    formValues: {},
  },
  signature: {
    formValues: {
      signatoryName: '',
      additionalSignatories: [],
    },
  },
}

const commercialAgreementTemplateReview = createSlice({
  name: sliceName,
  initialState: INITIAL_STATE,
  reducers: {
    initialise(
      state,
      action: PayloadAction<{
        commercialAgreementTemplate: CommercialAgreementTemplateItem
        currentUser: SessionMetadataUser
      }>,
    ) {
      const { commercialAgreementTemplate } = action.payload

      // Don't initialise if already initialised
      const hasAlreadyBeenInitialised =
        state.isInitialised &&
        commercialAgreementTemplate.id === state.commercialAgreementTemplate?.id

      if (hasAlreadyBeenInitialised) {
        return state
      }

      state.isInitialised = true

      // Initialise the commercialAgreementTemplate
      state.commercialAgreementTemplate = commercialAgreementTemplate

      // Initialise the current step
      state.currentStep =
        commercialAgreementTemplate.overviewFields.length > 0
          ? COMMERCIAL_AGREEMENT_REVIEW_STEP.overview
          : COMMERCIAL_AGREEMENT_REVIEW_STEP.placeholders

      // Initialise the placeholders
      state.placeholders.formValues = initialisePlaceholderStepFormValues(
        commercialAgreementTemplate,
      )

      // Initialise the signatureStep
      state.signature.formValues = initialiseSignatureStepFormValues(
        commercialAgreementTemplate,
      )
    },

    reset(_state) {
      return INITIAL_STATE
    },

    setCurrentStep(
      state,
      action: PayloadAction<
        CommercialAgreementTemplateReviewState['currentStep']
      >,
    ) {
      state.currentStep = action.payload
    },

    setCommercialAgreementTemplate(
      state,
      action: PayloadAction<
        CommercialAgreementTemplateReviewState['commercialAgreementTemplate']
      >,
    ) {
      state.commercialAgreementTemplate = action.payload
    },

    setPlaceholdersFormValues(
      state,
      action: PayloadAction<
        CommercialAgreementTemplateReviewState['placeholders']['formValues']
      >,
    ) {
      state.placeholders.formValues = action.payload
    },

    setSignatureFormValues(
      state,
      action: PayloadAction<
        CommercialAgreementTemplateReviewState['signature']['formValues']
      >,
    ) {
      state.signature.formValues = action.payload
    },
  },
})

export const {
  initialise,
  reset,
  setCurrentStep,
  setCommercialAgreementTemplate,
  setPlaceholdersFormValues,
  setSignatureFormValues,
} = commercialAgreementTemplateReview.actions

export type CommercialAgreementTemplateReviewStep =
  | 'overview'
  | 'placeholders'
  | 'pdfPreview'
  | 'signature'
  | 'commercialTermsActiveStep'

export const selectCommercialAgreementReview = (state: RootState) =>
  state.commercialAgreementTemplateReview

export const selectCurrentStep = createSelector(
  selectCommercialAgreementReview,
  (state) => state.currentStep,
)

export const selectCommercialAgreementTemplate = createSelector(
  selectCommercialAgreementReview,
  (state) => state.commercialAgreementTemplate,
)

export const selectSteps = createSelector(
  selectCommercialAgreementTemplate,
  (agreementTemplate): CommercialAgreementTemplateReviewStep[] => {
    if (
      agreementTemplate?.metadata.hasAlreadyBeenAccepted &&
      !agreementTemplate?.metadata.pendingRevision
    ) {
      return [COMMERCIAL_AGREEMENT_REVIEW_STEP.commercialTermsActiveStep]
    }

    const steps: CommercialAgreementTemplateReviewStep[] = []

    const overviewFields = agreementTemplate?.overviewFields || []
    const placeholders = agreementTemplate?.placeholders || []

    if (overviewFields.length > 0) {
      steps.push(COMMERCIAL_AGREEMENT_REVIEW_STEP.overview)
    }

    if (placeholders.length > 0) {
      steps.push(COMMERCIAL_AGREEMENT_REVIEW_STEP.placeholders)
    }

    steps.push(
      COMMERCIAL_AGREEMENT_REVIEW_STEP.pdfPreview,
      COMMERCIAL_AGREEMENT_REVIEW_STEP.signature,
      COMMERCIAL_AGREEMENT_REVIEW_STEP.commercialTermsActiveStep,
    )

    return steps
  },
)

export const selectNextStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    return getNextStep<CommercialAgreementTemplateReviewStep[]>(
      steps,
      currentStep,
    )
  },
)

export const selectPreviousStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    return getPreviousStep<CommercialAgreementTemplateReviewStep[]>(
      steps,
      currentStep,
    )
  },
)

export const selectHasNextStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    const nextStep = getNextStep<CommercialAgreementTemplateReviewStep[]>(
      steps,
      currentStep,
    )

    return nextStep !== currentStep
  },
)

export const selectHasPreviousStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    // If we've reached the commercial terms active step, the agreement has
    // already been accepted so don't allow going back to the previous steps
    if (
      currentStep === COMMERCIAL_AGREEMENT_REVIEW_STEP.commercialTermsActiveStep
    ) {
      return false
    }

    const previousStep = getPreviousStep<
      CommercialAgreementTemplateReviewStep[]
    >(steps, currentStep)

    return previousStep !== currentStep
  },
)

export const selectIsLastStep = createSelector(
  selectSteps,
  selectCurrentStep,
  (steps, currentStep) => {
    return currentStep === steps[steps.length - 1]
  },
)

export const selectPlaceholders = createSelector(
  selectCommercialAgreementReview,
  (state) => state.placeholders,
)

export const selectPlaceholdersFormValues = createSelector(
  selectPlaceholders,
  (placeholders) => placeholders.formValues,
)

export const selectSignatureFormValues = createSelector(
  selectCommercialAgreementReview,
  (state) => state.signature.formValues,
)

export const commercialAgreementTemplateReducer =
  commercialAgreementTemplateReview.reducer

function initialisePlaceholderStepFormValues(
  commercialAgreementTemplate: CommercialAgreementTemplateItem,
): PlaceholdersFormValues {
  const placeholders: PlaceholdersFormValues = {}

  commercialAgreementTemplate.placeholders.forEach((placeholder) => {
    placeholders[placeholder.token] = placeholder.prefilledValue || ''
  })

  return placeholders
}

function initialiseSignatureStepFormValues(
  commercialAgreementTemplate: CommercialAgreementTemplateItem,
): SignatureFormValues {
  const additionalSignatories =
    commercialAgreementTemplate.metadata.additionalSignatories.map(
      (additionalSignatory): AdditionalSignatoryFormRow => {
        return {
          name: additionalSignatory.name || '',
          signature: '',
          jobTitle: additionalSignatory.jobTitle || '',
          isAuthorisedToSign: false,
        }
      },
    )

  return {
    signatoryName: '',
    additionalSignatories,
  }
}
